import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/DataTable";
import { ListTree, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Listini() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [kindFilter, setKindFilter] = useState("tutti");

  const load = () => api.get("/listini").then((r) => setItems(r.data.map((l) => ({
    ...l,
    codes_text: [
      (l.voci || []).map((v) => v.code).filter(Boolean).join(" "),
      (l.voci || []).map((v) => v.description).filter(Boolean).join(" "),
    ].join(" "),
  }))));
  useEffect(() => { load(); }, []);

  const remove = async (id) => { try { await api.delete(`/listini/${id}`); toast.success("Eliminato"); load(); } catch (e) { toast.error("Errore"); } };

  const filtered = kindFilter === "tutti" ? items : items.filter((l) => (l.kind || "attivo") === kindFilter);

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Listini</h1>
          <p className="mt-1 text-sm text-slate-500">Voci di capitolato con prezzo unitario, usate per calcolare i ricavi.</p>
        </div>
        <Button onClick={() => navigate("/listini/nuovo")} className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]" data-testid="add-listino-btn">
          <Plus className="mr-2 h-4 w-4" /> Nuovo listino
        </Button>
      </div>
      <Card className="rounded-lg border-slate-200 shadow-sm">
        <div className="p-4">
          <div className="mb-3 flex gap-2" data-testid="listino-kind-filter">
            {[["tutti", "Tutti"], ["attivo", "Attivi"], ["passivo", "Passivi"]].map(([k, lbl]) => (
              <Button key={k} size="sm" variant={kindFilter === k ? "default" : "outline"} onClick={() => setKindFilter(k)} data-testid={`kind-filter-${k}`} className={`rounded-md ${kindFilter === k ? "bg-[#002FA7] text-white hover:bg-[#00227A]" : ""}`}>{lbl}</Button>
            ))}
          </div>
          <DataTable data={filtered} testid="listini-table" rowTestId={(l) => `listino-row-${l.id}`}
            searchKeys={["name", "codes_text"]} searchPlaceholder="Cerca listino o codice voce..." initialSort={{ key: "name", dir: "asc" }}
            onRowClick={(l) => navigate(`/listini/${l.id}`)}
            emptyIcon={ListTree} emptyText="Nessun listino"
            columns={[
              { key: "name", header: "Nome", sortable: true, className: "font-medium text-slate-900", cell: (l) => l.name },
              { key: "kind", header: "Tipo", sortable: true, cell: (l) => (l.kind || "attivo") === "passivo"
                  ? <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700">Passivo</Badge>
                  : <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">Attivo</Badge> },
              { key: "voci", header: "Voci", sortable: true, sortValue: (l) => l.voci.length, className: "text-slate-600", cell: (l) => `${l.voci.length} voci` },
              { key: "codes", header: "Codici voci", className: "text-slate-500", cell: (l) => {
                  const codes = (l.voci || []).map((v) => v.code).filter(Boolean);
                  if (!codes.length) return <span className="text-slate-300">—</span>;
                  const shown = codes.slice(0, 8);
                  return <span className="text-xs" data-testid={`listino-codes-${l.id}`}>{shown.join(", ")}{codes.length > 8 ? ` +${codes.length - 8}` : ""}</span>;
                } },
              { key: "actions", header: "Azioni", align: "right", cell: (l) => (
                <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/listini/${l.id}`)} data-testid={`edit-listino-${l.id}`} className="rounded-md"><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(l.id)} data-testid={`delete-listino-${l.id}`} className="rounded-md border-rose-200 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ) },
            ]} />
        </div>
      </Card>
    </div>
  );
}
